import {
  ANONYMOUS_VIEWER,
  savedAmount,
  viewerPackagePriceCents,
  type Currency,
  type Package,
  type PackageCategory,
  type PackageItem,
  type PriceViewer,
  type Product,
} from "@nh/shared";

/**
 * Resolver precios, repartir por categoría y emparejar los productos de
 * un paquete con el catálogo. Funciones PURAS, sin React: se prueban con
 * un array y sin montar la pantalla, que es donde estos errores —la
 * moneda que no existe, el ahorro que sale negativo— se esconden.
 */

export interface PackagePrice {
  /** Lo que se cobra, en centavos. Del servidor. */
  cents: number;
  /** La suma de sus productos a precio normal. Del servidor. */
  listCents: number | null;
  /**
   * Cuánto se ahorra, en centavos. `null` cuando no hay lista con la que
   * comparar o cuando la diferencia es cero — un "ahorras $0.00" no es un
   * argumento de venta, es una errata.
   */
  savedCents: number | null;
  /** La moneda en la que este precio está expresado de verdad. */
  currency: Currency;
  /** `true` cuando el paquete no tiene precio en la moneda que se pidió. */
  isFallbackCurrency: boolean;
  /** `true` cuando ya lleva el descuento de franquiciatario (14-sep). */
  isFranchiseePrice: boolean;
}

/**
 * El precio de un paquete en la moneda activa.
 *
 * ── Aquí no se calcula nada, se ELIGE la moneda ──────────────────────
 * `prices`, `listPrices` y el ahorro salen ya resueltos del servidor: el
 * contrato es explícito en que el precio se deriva del `discountPercent`
 * allá y no aquí, porque un precio tecleado dos veces se desincroniza y
 * la tienda acaba prometiendo un ahorro que no existe. Lo único que hace
 * esta función es decidir CUÁL de las dos monedas se enseña y arrastrar
 * lista y precio juntos, que es el error clásico: el precio en pesos y el
 * tachado en dólares, y un ahorro de miles.
 *
 * `savedAmount` viene de `@nh/shared` y solo resta dos números del
 * servidor. Es presentación, no una segunda fuente de verdad.
 *
 * Devuelve `null` si el paquete no tiene NINGÚN precio: eso no se pinta
 * como "$0.00" —que se lee como gratis— sino como "precio a consultar".
 */
export function packagePriceFor(
  pkg: Package,
  currency: Currency,
  viewer: Pick<PriceViewer, "isFranchisee"> = ANONYMOUS_VIEWER,
): PackagePrice | null {
  const other: Currency = currency === "MXN" ? "USD" : "MXN";
  const preferred = pkg.prices[currency];
  const resolvedCurrency: Currency = preferred !== undefined ? currency : other;
  // El 10 % de franquiciatario también en paquetes (Anahí, 14-sep): la
  // misma función que cobra el servidor.
  const cents = viewerPackagePriceCents(pkg, resolvedCurrency, viewer);

  if (cents === undefined) return null;

  const listCents = pkg.listPrices[resolvedCurrency];
  const saved = savedAmount(pkg.listPrices, { [resolvedCurrency]: cents }, resolvedCurrency);

  return {
    cents,
    listCents: listCents ?? null,
    // Un tachado que no supera al precio no es una rebaja: es una captura
    // mal hecha, y enseñarlo haría dudar del precio de al lado.
    savedCents: saved !== null && saved > 0 ? saved : null,
    currency: resolvedCurrency,
    isFallbackCurrency: resolvedCurrency !== currency,
    isFranchiseePrice: Boolean(viewer.isFranchisee),
  };
}

/**
 * Los paquetes de UNA categoría, ordenados.
 *
 * Manda `sortOrder` —el orden que decidió quien administra el catálogo— y
 * a igualdad, lo más reciente arriba. Ordena una COPIA: el array que llega
 * es el de la caché de TanStack Query y ordenarlo en el sitio cambiaría
 * los datos de cualquier otro componente que lea esa misma caché.
 */
/**
 * TODOS los paquetes, en un solo orden (Anahí, 28-ago: "en /paquetes
 * deben mostrarse todos, hasta los mayoristas"). La clasificación se
 * decide después, desde el panel; aquí solo se enseña como etiqueta.
 */
export function allPackagesSorted(packages: readonly Package[]): Package[] {
  return [...packages].sort(
    (a, b) => a.sortOrder - b.sortOrder || millis(b.createdAt) - millis(a.createdAt),
  );
}

export function packagesOfCategory(
  packages: readonly Package[],
  category: PackageCategory,
): Package[] {
  return packages
    .filter((pkg) => pkg.category === category)
    .sort((a, b) => a.sortOrder - b.sortOrder || millis(b.createdAt) - millis(a.createdAt));
}

function millis(iso: string): number {
  const value = Date.parse(iso);
  return Number.isNaN(value) ? 0 : value;
}

/**
 * El catálogo indexado por `id`, para poder poner NOMBRE a los productos
 * de un paquete.
 *
 * `packageItemSchema` guarda `productId` y cantidad, nada más — y con
 * razón: el nombre de un producto cambia y un paquete no debería quedarse
 * con una copia vieja. El precio que se enseña sigue siendo el del
 * paquete, así que este emparejamiento es solo para el texto.
 */
export function indexProducts(products: readonly Product[]): Map<string, Product> {
  return new Map(products.map((product) => [product.id, product]));
}

export interface ResolvedPackageItem {
  productId: string;
  quantity: number;
  /** `null` cuando el catálogo todavía no llegó o el producto ya no está. */
  product: Product | null;
}

/**
 * Los productos de un paquete, emparejados con el catálogo.
 *
 * Un `productId` sin pareja NO se descarta: se devuelve con `product:
 * null` para que la tarjeta pueda seguir diciendo cuántas piezas trae el
 * paquete. Filtrarlo haría que un paquete de cinco productos se anunciara
 * como de cuatro porque uno se despublicó, y el número de piezas es parte
 * de lo que se está comprando.
 */
export function resolveItems(
  items: readonly PackageItem[],
  byId: Map<string, Product> | null,
): ResolvedPackageItem[] {
  return items.map((item) => ({
    productId: item.productId,
    quantity: item.quantity,
    product: byId?.get(item.productId) ?? null,
  }));
}

/** Cuántas piezas trae el paquete en total (suma de cantidades). */
export function totalUnits(items: readonly PackageItem[]): number {
  return items.reduce((sum, item) => sum + item.quantity, 0);
}

/**
 * A dónde lleva elegir un paquete.
 *
 * ── El parámetro es el encargo, no un adorno ─────────────────────────
 * El documento pide que seleccionar un paquete lleve al registro «con el
 * paquete preseleccionado», no al registro a secas. `/register` todavía
 * NO lee este parámetro —lo hará el wizard cuando le toque—, pero el
 * enlace lo lleva desde hoy: así el día que el registro lo lea, esta
 * pantalla ya está en su sitio y no hay que volver a tocarla.
 *
 * El slug se codifica: viaja en la cadena de consulta y un `&` o un `#`
 * sin codificar partirían la URL en dos.
 */
export function registerHrefFor(slug: string): string {
  return `/register?package=${encodeURIComponent(slug)}`;
}
